import React, { useMemo, useRef, useCallback } from "react";
import "./DietaBalanceada.css";
import { useDietaBalanceadaController } from "../../../controllers/DietaBalanceadaController";
import { optimizarDietaLP } from "../../../controllers/optimizadorLP";

export default function DietaBalanceada({
    DietasController,
    datosAnimal,
    reqNutricional,
    restriccionMateria,
    onAtras,
}) {
    const { infoGeneral, materiasConDetalles, detalleKeys } =
        useDietaBalanceadaController(DietasController, datosAnimal);

    const fmtNum = (v, d = 2) =>
        v === null || v === undefined || v === ""
            ? "—"
            : Number.isFinite(Number(v))
                ? Number(v).toFixed(d)
                : String(v);

    const rnRows = Array.isArray(reqNutricional?.rows) ? reqNutricional.rows : [];
    const rnParentRows = rnRows.filter((r) => !r.isChild);
    const restrRows = useMemo(
        () => (Array.isArray(restriccionMateria?.rows) ? restriccionMateria.rows : []),
        [restriccionMateria]
    );

    // Resultado LP
    const resultadoLP = useMemo(() => {
        try {
            return optimizarDietaLP(
                materiasConDetalles || [],
                restrRows || [],
                reqNutricional || null
            );
        } catch (e) {
            return { ok: false, msg: e?.message || "Error LP" };
        }
    }, [materiasConDetalles, restrRows, reqNutricional]);

    const { porMateria, totales, reqObtenidoById, estadoById, ok: lpOK, msg: lpMsg } =
        resultadoLP || {};

    const renderEstado = (st) => {
        if (st === "OK") return <span className="dbal-ok">OK</span>;
        if (st === "BAJO") return <span className="dbal-low">Bajo</span>;
        if (st === "ALTO") return <span className="dbal-high">Alto</span>;
        return "—";
    };

    // KPI
    const { bajos, oks, altos } = useMemo(() => {
        let bajos = 0,
            oks = 0,
            altos = 0;
        for (const r of rnParentRows) {
            const st = estadoById?.[r.id];
            if (st === "BAJO") bajos++;
            else if (st === "ALTO") altos++;
            else if (st === "OK") oks++;
        }
        return { bajos, oks, altos };
    }, [estadoById, rnParentRows]);

    // Resumen de materias
    const resumenMaterias = useMemo(() => {
        const lista = [];
        const totalKg = Number(totales?.materiaapvinculaciontotal || 0);
        const costoById = {};
        for (const r of restrRows) {
            if (r && r.idMateria != null)
                costoById[String(r.idMateria)] = Number(r.costo || 0);
        }
        for (const m of materiasConDetalles || []) {
            const pm = porMateria?.[m.id];
            const kg = pm?.materiaapvinculacion || 0;
            if (kg <= 0) continue;
            const costoUnit = costoById[String(m.id)] ?? 0;
            const total$ = kg * costoUnit;
            const pct = totalKg > 0 ? (kg / totalKg) * 100 : 0;
            lista.push({
                id: m.id,
                nombre: m.nombre,
                kg,
                costoUnit,
                total$,
                pct,
                tipo: m.temporal ? "Temporal" : "Banco",
            });
        }
        lista.sort((a, b) => b.kg - a.kg);
        const sumKg = lista.reduce((acc, it) => acc + it.kg, 0);
        const sumPct = lista.reduce((acc, it) => acc + it.pct, 0);
        const sum$ = lista.reduce((acc, it) => acc + it.total$, 0);
        return { lista, totalKg, sumKg, sumPct, sum$ };
    }, [materiasConDetalles, porMateria, restrRows, totales?.materiaapvinculaciontotal]);

    const fechaHoy = new Date().toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    // PDF landscape
    const pdfRef = useRef(null);
    const handleDownloadPDF = useCallback(async () => {
        try {
            const mod = await import("html2pdf.js");
            const html2pdfLib = mod?.default || window.html2pdf;
            if (!html2pdfLib) return alert("No se encontró html2pdf.js");
            const element = pdfRef.current;
            const opt = {
                margin: [10, 10, 10, 10],
                filename: "NutriPerformance_Dieta.pdf",
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
            };
            await html2pdfLib().from(element).set(opt).save();
        } catch (err) {
            console.error(err);
        }
    }, []);

    return (
        <div className="dbal">
            <div ref={pdfRef}>
                {/* Encabezado */}
                <header className="dbal-print-header-elegant">
                    <img src="/logo.png" alt="Logo" className="dbal-logo-elegant" />
                    <div className="dbal-title-box">
                        <h1 className="dbal-main-title">NUTRIPERFORMANCE</h1>
                        <h2 className="dbal-sub-title">DIETA BALANCEADA</h2>
                        <div className="dbal-gradient-line" />
                    </div>
                    <div className="dbal-date-box">
                        <span className="dbal-date-label">Fecha:</span>
                        <span className="dbal-date-value">{fechaHoy}</span>
                    </div>
                </header>

                <p className="dbal-subtitle-institucional">
                    Universidad de Cundinamarca — Proyecto NutriPerformance
                </p>

                {/* Información general */}
                <section className="dbal-info">
                    <h2 className="dbal-title">Información General</h2>
                    <div className="dbal-grid">
                        <Cell label="Raza" value={infoGeneral.raza} />
                        <Cell label="Sistema" value={infoGeneral.sistema} />
                        <Cell label="Sexo" value={infoGeneral.sexo} />
                        <Cell label="Peso Inicial" value={fmtNum(infoGeneral.pesoInicial) + " kg"} />
                        <Cell label="Peso Final" value={fmtNum(infoGeneral.pesoFinal) + " kg"} />
                        <Cell label="Ganancia Diaria" value={fmtNum(infoGeneral.pesoDiario) + " kg/d"} />
                        <Cell label="Temperatura" value={fmtNum(infoGeneral.tempMax) + " °C"} />
                        <Cell label="Humedad" value={fmtNum(infoGeneral.humedad) + " %"} />
                    </div>
                </section>

                {/* Estado de nutrientes */}
                <section className="dbal-kpis">
                    <h2 className="dbal-title">Estado de nutrientes</h2>
                    <div className="dbal-kpi-row">
                        <div className="dbal-kpi dbal-kpi-low">
                            <div className="dbal-kpi-title">Baja de nutrientes necesarios</div>
                            <div className="dbal-kpi-desc">Uno o más nutrientes están por debajo del requerimiento.</div>
                            <div className="dbal-kpi-num">{bajos || 0}</div>
                        </div>
                        <div className="dbal-kpi dbal-kpi-ok">
                            <div className="dbal-kpi-title">Nutrientes necesarios</div>
                            <div className="dbal-kpi-desc">Todos los nutrientes dentro de los rangos objetivos.</div>
                            <div className="dbal-kpi-num">{oks || 0}</div>
                        </div>
                        <div className="dbal-kpi dbal-kpi-high">
                            <div className="dbal-kpi-title">Sobrepasa los requisitos</div>
                            <div className="dbal-kpi-desc">Alguno(s) superan el máximo permitido.</div>
                            <div className="dbal-kpi-num">{altos || 0}</div>
                        </div>
                    </div>
                </section>

                {/* Requisitos nutricionales */}
                <section className="dbal-rn">
                    <h2 className="dbal-title">Requisitos nutricionales utilizados</h2>
                    <div className="dbal-table-container">
                        <table className="dbal-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Req. nutricional</th>
                                    <th>Sigla</th>
                                    <th>Unidad</th>
                                    <th>Req. Mínimo</th>
                                    <th>Req. Máximo</th>
                                    <th>Req. Obtenido</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rnParentRows.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.id}</td>
                                        <td>{r.nombre}</td>
                                        <td>{r.sigla}</td>
                                        <td>{r.unidad}</td>
                                        <td>{fmtNum(r.min)}</td>
                                        <td>{fmtNum(r.max)}</td>
                                        <td>{fmtNum(reqObtenidoById?.[r.id])}</td>
                                        <td>{renderEstado(estadoById?.[r.id])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Requisito de agua */}
                    {(() => {
                        const aguaRow = rnParentRows.find((r) => String(r.id) === "22");
                        if (!aguaRow) return null;
                        return (
                            <p className="dbal-req-agua">
                                Requisito diario de agua para el animal:{" "}
                                <strong>
                                    {fmtNum(aguaRow.min)} {aguaRow.unidad || "L/día"}
                                </strong>
                            </p>
                        );
                    })()}
                </section>

                {/* Resumen */}
                <section className="dbal-sol">
                    <h2 className="dbal-title">Resumen de materias y costos</h2>
                    <div className="dbal-table-container">
                        <table className="dbal-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Materia Prima</th>
                                    <th>KG usados</th>
                                    <th>Valor unitario ($/kg)</th>
                                    <th>Total ($)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resumenMaterias.lista.map((row, i) => (
                                    <tr key={row.id}>
                                        <td>{i + 1}</td>
                                        <td>{row.nombre}</td>
                                        <td>{fmtNum(row.kg)}</td>
                                        <td>{fmtNum(row.costoUnit)}</td>
                                        <td>{fmtNum(row.total$)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={2}>Totales:</td>
                                    <td>{fmtNum(resumenMaterias.sumKg)}</td>
                                    <td>—</td>
                                    <td>{fmtNum(resumenMaterias.sum$)}</td>
                                </tr>
                                <tr>
                                    <td colSpan={2}>TOTAL DE DIETA</td>
                                    <td>{fmtNum(resumenMaterias.totalKg)}</td>
                                    <td colSpan={2}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </section>

                {/* Participación porcentual */}
                <section className="dbal-comp">
                    <h2 className="dbal-title">Participación de materias en la dieta</h2>
                    <div className="dbal-table-container">
                        <table className="dbal-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Materia Prima</th>
                                    <th>KG usados</th>
                                    <th>% de participación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resumenMaterias.lista.map((row, i) => (
                                    <tr key={row.id}>
                                        <td>{i + 1}</td>
                                        <td>{row.nombre}</td>
                                        <td>{fmtNum(row.kg)}</td>
                                        <td>{fmtNum(row.pct, 2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={3}>Suma de porcentajes:</td>
                                    <td>{fmtNum(resumenMaterias.sumPct, 2)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </section>
            </div>

            {/* Botones */}
            <div className="dbal-actions no-print">
                <button className="dbal-btn" onClick={onAtras}>
                    Atrás
                </button>
                <button className="print-btn" onClick={handleDownloadPDF}>
                    <svg
                        className="print-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                    >
                        <path d="M19 8H5c-1.66 0-3 1.34-3 3v4h4v4h12v-4h4v-4c0-1.66-1.34-3-3-3zm-3 11H8v-6h8v6zm3-9a1 1 0 110-2 1 1 0 010 2zM17 3H7v4h10V3z" />
                    </svg>
                    Descargar PDF
                </button>
            </div>
        </div>
    );
}

function Cell({ label, value }) {
    return (
        <div className="dbal-cell">
            <span className="dbal-label">{label}:</span>
            <span className="dbal-value">{value ?? "—"}</span>
        </div>
    );
}